import React, { useState, useEffect, useCallback, useRef, FormEvent } from 'react';
import { apiGet, apiPatch, apiPost, apiPut, apiDelete } from '../api/client';
import { formatPhone, formatPhoneInput } from '../utils/formatPhone';
import type {
  EmergencyContactListResponse,
  EmergencyContactResponse,
  CreateEmergencyContactRequest,
  UpdateEmergencyContactRequest,
} from '../types/api';
import { VaultRole } from '../types/api';
import AlertMessage from './AlertMessage';
import LoadingSpinner from './LoadingSpinner';
import Skeleton from './Skeleton';
import EmptyState from './EmptyState';
import SortableList from './SortableList';
import './EmergencyContactsList.css';

interface Props {
  vaultId: string;
  userRole: VaultRole;
  onCountChange?: (count: number) => void;
}

export default function EmergencyContactsList({ vaultId, userRole, onCountChange }: Props) {
  const onCountChangeRef = useRef(onCountChange);
  onCountChangeRef.current = onCountChange;

  const [contacts, setContacts] = useState<EmergencyContactResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  // Create form state
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editRelationship, setEditRelationship] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editCompany, setEditCompany] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState<EmergencyContactResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  const canEdit = userRole === VaultRole.Owner || userRole === VaultRole.Spouse;

  const loadContacts = useCallback(async () => {
    try {
      const data = await apiGet<EmergencyContactListResponse>(`/api/vault/${vaultId}/emergency-contacts`);
      setContacts(data.contacts);
      onCountChangeRef.current?.(data.contacts.length);
    } catch (err: any) {
      setError(err.message || 'Failed to load contacts.');
    } finally {
      setLoading(false);
    }
  }, [vaultId]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  async function handleReorder(reordered: EmergencyContactResponse[]) {
    setContacts(reordered);
    try {
      await apiPatch(`/api/vault/${vaultId}/emergency-contacts/reorder`, {
        orderedIds: reordered.map(c => c.id),
      });
    } catch {
      await loadContacts();
    }
  }

  function handleExpand(id: string) {
    if (expandedId === id) {
      setExpandedId(null);
      setEditingId(null);
      return;
    }
    setExpandedId(id);
    setEditingId(null);
  }

  function startEdit(c: EmergencyContactResponse) {
    setEditingId(c.id);
    setEditName(c.name);
    setEditRelationship(c.relationship);
    setEditPhone(c.phone || '');
    setEditEmail(c.email || '');
    setEditCompany(c.company || '');
    setEditNotes(c.notes || '');
  }

  function cancelEdit() {
    setEditingId(null);
  }

  function resetCreateForm() {
    setName('');
    setRelationship('');
    setPhone('');
    setEmail('');
    setCompany('');
    setNotes('');
  }

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      const body: CreateEmergencyContactRequest = {
        name,
        relationship,
        phone: phone || null,
        email: email || null,
        company: company || null,
        notes: notes || null,
      };
      await apiPost(`/api/vault/${vaultId}/emergency-contacts`, body);
      resetCreateForm();
      setShowForm(false);
      await loadContacts();
    } catch (err: any) {
      setError(err.message || 'Failed to create contact.');
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editingId) return;
    setError('');
    setEditSaving(true);

    try {
      const body: UpdateEmergencyContactRequest = {
        name: editName,
        relationship: editRelationship,
        phone: editPhone || null,
        email: editEmail || null,
        company: editCompany || null,
        notes: editNotes || null,
      };
      await apiPut(`/api/vault/${vaultId}/emergency-contacts/${editingId}`, body);
      setEditingId(null);
      setExpandedId(null);
      await loadContacts();
    } catch (err: any) {
      setError(err.message || 'Failed to update contact.');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    setDeleting(true);
    setError('');
    try {
      await apiDelete(`/api/vault/${vaultId}/emergency-contacts/${confirmDelete.id}`);
      setConfirmDelete(null);
      if (expandedId === confirmDelete.id) {
        setExpandedId(null);
        setEditingId(null);
      }
      await loadContacts();
    } catch (err: any) {
      setError(err.message || 'Delete failed.');
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <Skeleton variant="row" count={3} />;

  return (
    <div>
      <div className="contacts-header">
        <h3>Emergency Contacts</h3>
        <button
          className="btn-text"
          onClick={() => canEdit && setShowForm(!showForm)}
          disabled={!canEdit}
        >
          {showForm ? 'Cancel' : '+ Add Contact'}
        </button>
      </div>

      <p className="contacts-description">
        Important people to contact when something happens &mdash; attorney,
        doctor, financial advisor, pastor, funeral home, insurance agent, and more.
      </p>

      <AlertMessage type="error" message={error} />

      {showForm && (
        <form className="contact-form" onSubmit={handleCreate}>
          <div className="form-group">
            <label className="form-label" htmlFor="contactName">Name</label>
            <input
              id="contactName"
              className="form-input"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g., Dr. Jane Smith"
              required
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contactRelationship">Relationship</label>
            <input
              id="contactRelationship"
              className="form-input"
              type="text"
              value={relationship}
              onChange={e => setRelationship(e.target.value)}
              placeholder="e.g., Attorney, Doctor, Pastor"
              required
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contactPhone">Phone</label>
            <input
              id="contactPhone"
              className="form-input"
              type="tel"
              value={phone}
              onChange={e => setPhone(formatPhoneInput(e.target.value))}
              placeholder="(555) 123-4567"
              maxLength={14}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contactEmail">Email</label>
            <input
              id="contactEmail"
              className="form-input"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="jane@example.com"
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contactCompany">Company</label>
            <input
              id="contactCompany"
              className="form-input"
              type="text"
              value={company}
              onChange={e => setCompany(e.target.value)}
              placeholder="e.g., Smith & Associates"
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="contactNotes">Notes</label>
            <textarea
              id="contactNotes"
              className="form-input"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details..."
              maxLength={2000}
            />
          </div>
          <div className="contact-form-actions">
            <button className="btn-secondary" type="button" onClick={() => {
              setShowForm(false);
              resetCreateForm();
            }}>
              Cancel
            </button>
            <button className="btn-primary" type="submit" disabled={saving}>
              {saving ? <LoadingSpinner small /> : 'Save Contact'}
            </button>
          </div>
        </form>
      )}

      {contacts.length === 0 ? (
        <EmptyState
          icon="contacts"
          title="No emergency contacts yet"
          subtitle={canEdit ? "Click '+ Add Contact' above to add one." : undefined}
        />
      ) : (
        <div className="contacts-list">
          <SortableList
            items={contacts}
            canReorder={canEdit}
            onReorder={handleReorder}
            renderItem={(c) => (
            <div className={`contact-item${expandedId === c.id ? ' contact-item--expanded' : ''}`}>
              <div className="contact-item-header" onClick={() => handleExpand(c.id)}>
                <div className="contact-item-icon">&#128222;</div>
                <div className="contact-item-info">
                  <div className="contact-item-name">
                    {c.name}
                    <span className="contact-relationship-badge">{c.relationship}</span>
                  </div>
                  {c.phone && (
                    <div className="contact-item-phone">{formatPhone(c.phone)}</div>
                  )}
                </div>
                <span className="contact-item-chevron">{expandedId === c.id ? '\u25B2' : '\u25BC'}</span>
              </div>

              {expandedId === c.id && (
                <div className="contact-item-body">
                  {editingId === c.id ? (
                    <form className="contact-edit-form" onSubmit={handleSaveEdit}>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactName">Name</label>
                        <input
                          id="editContactName"
                          className="form-input"
                          type="text"
                          value={editName}
                          onChange={e => setEditName(e.target.value)}
                          required
                          maxLength={200}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactRelationship">Relationship</label>
                        <input
                          id="editContactRelationship"
                          className="form-input"
                          type="text"
                          value={editRelationship}
                          onChange={e => setEditRelationship(e.target.value)}
                          required
                          maxLength={100}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactPhone">Phone</label>
                        <input
                          id="editContactPhone"
                          className="form-input"
                          type="tel"
                          value={editPhone}
                          onChange={e => setEditPhone(formatPhoneInput(e.target.value))}
                          maxLength={14}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactEmail">Email</label>
                        <input
                          id="editContactEmail"
                          className="form-input"
                          type="email"
                          value={editEmail}
                          onChange={e => setEditEmail(e.target.value)}
                          maxLength={200}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactCompany">Company</label>
                        <input
                          id="editContactCompany"
                          className="form-input"
                          type="text"
                          value={editCompany}
                          onChange={e => setEditCompany(e.target.value)}
                          maxLength={200}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor="editContactNotes">Notes</label>
                        <textarea
                          id="editContactNotes"
                          className="form-input"
                          value={editNotes}
                          onChange={e => setEditNotes(e.target.value)}
                          maxLength={2000}
                        />
                      </div>
                      <div className="contact-form-actions">
                        <button className="btn-secondary" type="button" onClick={cancelEdit} disabled={editSaving}>
                          Cancel
                        </button>
                        <button className="btn-primary" type="submit" disabled={editSaving}>
                          {editSaving ? <LoadingSpinner small /> : 'Save Changes'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      {c.phone && (
                        <div className="contact-detail-row">
                          <div className="contact-detail-label">Phone</div>
                          <div className="contact-detail-value">
                            <a href={`tel:${c.phone}`}>{formatPhone(c.phone)}</a>
                          </div>
                        </div>
                      )}
                      {c.email && (
                        <div className="contact-detail-row">
                          <div className="contact-detail-label">Email</div>
                          <div className="contact-detail-value">
                            <a href={`mailto:${c.email}`}>{c.email}</a>
                          </div>
                        </div>
                      )}
                      {c.company && (
                        <div className="contact-detail-row">
                          <div className="contact-detail-label">Company</div>
                          <div className="contact-detail-value">{c.company}</div>
                        </div>
                      )}
                      {c.notes && (
                        <div className="contact-detail-row">
                          <div className="contact-detail-label">Notes</div>
                          <div className="contact-detail-value">{c.notes}</div>
                        </div>
                      )}
                      <div className="contact-item-actions">
                        <button className="contact-edit-btn" onClick={() => startEdit(c)} disabled={!canEdit}>
                          Edit
                        </button>
                        <button className="contact-delete-btn" onClick={() => setConfirmDelete(c)} disabled={!canEdit}>
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            )}
          />
        </div>
      )}

      {confirmDelete && (
        <div className="contact-confirm-overlay" onClick={() => !deleting && setConfirmDelete(null)}>
          <div className="contact-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h4>Delete Contact</h4>
            <p>
              Are you sure you want to delete <strong>{confirmDelete.name}</strong>?
              This action cannot be undone.
            </p>
            <div className="contact-confirm-actions">
              <button
                className="contact-confirm-delete"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                className="contact-confirm-cancel"
                onClick={() => setConfirmDelete(null)}
                disabled={deleting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
